import LogoutIcon from '@mui/icons-material/Logout';
import {
  Badge,
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { routesApp } from 'shared/config';

import { NavItemWithActive, getNavAriaLabel } from './nav-items';

export const NAV_DRAWER_ID = 'primary-nav-drawer';

type Props = {
  open: boolean;
  onClose: () => void;
  items: NavItemWithActive[];
  newMessagesCount: number;
  /** Shown at the top of the drawer when the toolbar has no room for the identity block. */
  identity?: { role: string; email: string };
  onLogout?: () => void;
};

export const NavDrawer = ({ open, onClose, items, newMessagesCount, identity, onLogout }: Props) => (
  <Drawer
    id={NAV_DRAWER_ID}
    anchor="left"
    open={open}
    onClose={onClose}
    PaperProps={{ sx: { width: 280, maxWidth: '85vw' } }}
  >
    {identity && (
      <>
        <Box sx={{ px: 2, py: 1.5, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ color: 'primary.main', fontWeight: 500 }}>
            {identity.role}
          </Typography>
          <Typography variant="body2" noWrap title={identity.email}>
            {identity.email}
          </Typography>
        </Box>
        <Divider />
      </>
    )}
    <Box component="nav" aria-label="Primary navigation">
      <List sx={{ py: 1 }}>
        {items.map((item) => {
          const { label, path, icon: Icon, isActive } = item;
          const showBadge = path === routesApp.supportMessages && newMessagesCount > 0;
          return (
            <ListItemButton
              key={path}
              component={RouterLink}
              to={path}
              selected={isActive}
              onClick={onClose}
              aria-current={isActive ? 'page' : undefined}
              aria-label={getNavAriaLabel(item, newMessagesCount)}
              sx={{
                minHeight: 48,
                borderLeft: 3,
                borderLeftColor: isActive ? 'primary.main' : 'transparent',
                '&.Mui-selected': { color: 'primary.main', bgcolor: 'action.selected' },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                {showBadge ? (
                  <Badge badgeContent={newMessagesCount} color="error" max={99}>
                    <Icon />
                  </Badge>
                ) : (
                  <Icon />
                )}
              </ListItemIcon>
              <ListItemText primary={label} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
    {onLogout && (
      <>
        <Divider sx={{ mt: 'auto' }} />
        <List sx={{ py: 1 }}>
          <ListItemButton onClick={onLogout} sx={{ minHeight: 48 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Log out" />
          </ListItemButton>
        </List>
      </>
    )}
  </Drawer>
);
